import {
  Banknote,
  BadgeCheck,
  Check,
  Copy,
  Eraser,
  ReceiptText,
  ShieldCheck,
  createIcons
} from "lucide";
import "./styles.css";
import { convertRmbToUppercase } from "./rmb";

const DEFAULT_AMOUNT = "";
const SAMPLE_AMOUNTS = ["1288.00", "1001.01", "123456789.09"];
const STORAGE_KEYS = {
  amount: "rmb-tool.amount"
} as const;

const app = document.querySelector<HTMLDivElement>("#app");
const iconSet = {
  BadgeCheck,
  Banknote,
  Check,
  Copy,
  Eraser,
  ReceiptText,
  ShieldCheck
};

if (!app) {
  throw new Error("App mount point is missing.");
}

app.innerHTML = `
  <main class="shell">
    <header class="masthead">
      <div class="brandMark" aria-hidden="true">
        <i data-lucide="banknote"></i>
      </div>
      <div class="headline">
        <p class="eyebrow">RMB Uppercase</p>
        <h1>人民币大写转换</h1>
      </div>
    </header>

    <section class="workspace" aria-label="人民币金额转换工具">
      <section class="inputPane" aria-labelledby="amountLabel">
        <div class="fieldHeader">
          <label id="amountLabel" for="amountInput">金额</label>
          <span id="inputState" class="inputState">就绪</span>
        </div>

        <div class="amountField">
          <span class="currencySign" aria-hidden="true">¥</span>
          <input
            id="amountInput"
            type="text"
            inputmode="decimal"
            autocomplete="off"
            spellcheck="false"
            placeholder="0.00"
            aria-describedby="inputState"
          />
        </div>

        <div class="sampleBar" aria-label="示例金额">
          ${SAMPLE_AMOUNTS.map(
            (amount) => `
              <button class="sampleButton" type="button" data-amount="${amount}">
                ${amount}
              </button>
            `
          ).join("")}
        </div>

        <div class="detailList" aria-label="转换规则">
          <div>
            <i data-lucide="shield-check" aria-hidden="true"></i>
            <span>到分</span>
            <strong>两位小数</strong>
          </div>
          <div>
            <i data-lucide="receipt-text" aria-hidden="true"></i>
            <span>金额</span>
            <strong>自动规范</strong>
          </div>
          <div>
            <i data-lucide="badge-check" aria-hidden="true"></i>
            <span>复制</span>
            <strong>一键取用</strong>
          </div>
        </div>
      </section>

      <section class="outputPane" aria-labelledby="resultLabel">
        <div class="fieldHeader">
          <span id="resultLabel">大写结果</span>
          <span id="resultState" class="resultState" aria-live="polite">可复制</span>
        </div>

        <div class="resultCard">
          <output id="resultText" class="resultText" for="amountInput"></output>
        </div>
        <p id="normalizedText" class="normalizedText"></p>

        <div class="actions">
          <button id="copyButton" class="primaryAction" type="button" title="复制结果">
            <i data-lucide="copy" aria-hidden="true"></i>
            <span>复制</span>
          </button>
          <button id="clearButton" class="quietAction" type="button" title="清空金额">
            <i data-lucide="eraser" aria-hidden="true"></i>
            <span>清空</span>
          </button>
        </div>
      </section>
    </section>
  </main>
`;

renderIcons();

const amountInput = document.querySelector<HTMLInputElement>("#amountInput")!;
const inputState = document.querySelector<HTMLSpanElement>("#inputState")!;
const resultState = document.querySelector<HTMLSpanElement>("#resultState")!;
const resultText = document.querySelector<HTMLOutputElement>("#resultText")!;
const normalizedText = document.querySelector<HTMLParagraphElement>("#normalizedText")!;
const copyButton = document.querySelector<HTMLButtonElement>("#copyButton")!;
const clearButton = document.querySelector<HTMLButtonElement>("#clearButton")!;
const sampleButtons = document.querySelectorAll<HTMLButtonElement>(".sampleButton");

amountInput.value = localStorage.getItem(STORAGE_KEYS.amount) || DEFAULT_AMOUNT;

function render(): void {
  const result = convertRmbToUppercase(amountInput.value, {
    prefix: true,
    rounding: "strict"
  });

  localStorage.setItem(STORAGE_KEYS.amount, amountInput.value);

  if (!result.ok) {
    document.body.dataset.state = result.code === "empty" ? "idle" : "error";
    inputState.textContent = result.message;
    resultState.textContent = result.code === "empty" ? "待输入" : "需修正";
    resultText.textContent = result.code === "empty" ? "等待输入金额" : "无法转换";
    normalizedText.textContent =
      result.code === "precision" ? "小数最多保留到分" : "";
    copyButton.disabled = true;
    return;
  }

  document.body.dataset.state = "ready";
  inputState.textContent = "有效金额";
  resultState.textContent = "可复制";
  resultText.innerHTML = highlightLargeUnits(result.output);
  normalizedText.textContent = `规范金额 ${result.normalized}`;
  copyButton.disabled = false;
}

function highlightLargeUnits(value: string): string {
  return escapeHtml(value).replace(
    /([万亿兆京])/g,
    '<span class="majorUnit">$1</span>'
  );
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => {
    const entities: Record<string, string> = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      "\"": "&quot;",
      "'": "&#39;"
    };

    return entities[char];
  });
}

async function copyResult(): Promise<void> {
  if (copyButton.disabled || !resultText.textContent) {
    return;
  }

  try {
    await navigator.clipboard.writeText(resultText.textContent);
    showCopiedState();
  } catch {
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(resultText);
    selection?.removeAllRanges();
    selection?.addRange(range);
    showCopiedState("已选中");
  }
}

function showCopiedState(label = "已复制"): void {
  resultState.textContent = label;
  copyButton.classList.add("isCopied");
  copyButton.querySelector("span")!.textContent = label;
  setCopyButtonIcon("check");

  window.setTimeout(() => {
    copyButton.classList.remove("isCopied");
    copyButton.querySelector("span")!.textContent = "复制";
    setCopyButtonIcon("copy");
    render();
  }, 1400);
}

function setCopyButtonIcon(name: "copy" | "check"): void {
  copyButton.querySelector("svg, i")?.remove();
  copyButton.insertAdjacentHTML(
    "afterbegin",
    `<i data-lucide="${name}" aria-hidden="true"></i>`
  );
  renderIcons();
}

function renderIcons(): void {
  createIcons({ icons: iconSet });
}

amountInput.addEventListener("input", render);
copyButton.addEventListener("click", () => void copyResult());
clearButton.addEventListener("click", () => {
  amountInput.value = "";
  amountInput.focus();
  render();
});
sampleButtons.forEach((button) => {
  button.addEventListener("click", () => {
    amountInput.value = button.dataset.amount || "";
    amountInput.focus();
    render();
  });
});

render();
